import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useStatusOptions } from '../lib/useStatusOptions';
import { useLookup } from '../contexts/LookupContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  MagnifyingGlass, Plus, CaretLeft, CaretRight, Trash, EnvelopeSimple, Phone, DownloadSimple, PencilSimple
} from '@phosphor-icons/react';
import BulkEditDialog from '../components/BulkEditDialog';

const STATUS_COLORS = {
  lead: 'bg-amber-100 text-amber-800 border-amber-200',
  customer: 'bg-green-100 text-green-800 border-green-200',
  inactive: 'bg-gray-100 text-gray-600 border-gray-200',
  prospect: 'bg-blue-100 text-blue-800 border-blue-200',
};

export default function ContactsPage() {
  const { statuses: statusOptions } = useStatusOptions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeLookup, clearActiveLookup } = useLookup();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  const page = parseInt(searchParams.get('page') || '1');
  const search = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || '';
  const tagFilter = searchParams.get('tag') || '';

  // Local input state — the input must not be driven directly by the URL,
  // otherwise fast typing races router re-renders and drops keystrokes.
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => { setSearchInput(search); }, [search]);
  useEffect(() => {
    if (searchInput === search) return;
    const t = setTimeout(() => updateParams({ search: searchInput }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const [exporting, setExporting] = useState(false);

  // Export exactly what the list is showing (lookup / ids / filters)
  const handleExportView = async () => {
    setExporting(true);
    try {
      const body = activeLookup?.query
        ? { lookup: activeLookup.query }
        : activeLookup?.ids?.length
          ? { ids: activeLookup.ids }
          : { search, status: statusFilter, tag: tagFilter };
      const { data } = await api.post('/contacts/export/view', body, { responseType: 'blob' });
      const filename = `contacts_export_${new Date().toISOString().slice(0, 10)}.csv`;
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      const text = await data.text();
      let nl = 0, inQ = false;
      for (let i = 0; i < text.length; i++) {
        if (text[i] === '"') inQ = !inQ;
        else if (text[i] === '\n' && !inQ) nl++;
      }
      const records = text.endsWith('\n') ? nl : nl + 1;
      toast.success(`Exported ${Math.max(0, records - 1)} contacts — ${filename} saved to your Downloads folder`, { duration: 8000 });
    } catch (err) {
      console.error('Export failed', err);
      toast.error('Export failed — please try again');
    } finally {
      setExporting(false);
    }
  };

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      // ACT! 4.0: an active lookup is a persistent filter — the Contact
      // List shows the looked-up set, not the whole database.
      if (activeLookup?.query) {
        const { data } = await api.post(`/contacts/lookup?page=${page}&limit=50`, activeLookup.query);
        setContacts(data.contacts);
        setTotal(data.total);
        setPages(data.pages || Math.ceil((data.total || 0) / 50));
      } else {
        const params = { page, limit: 50, search, status: statusFilter, tag: tagFilter };
        if (activeLookup?.ids?.length) params.ids = activeLookup.ids.join(',');
        const { data } = await api.get('/contacts', { params });
        setContacts(data.contacts);
        setTotal(data.total);
        setPages(data.pages);
      }
    } catch (err) {
      console.error('Failed to fetch contacts', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, tagFilter, activeLookup]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const updateParams = (updates) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v) params.set(k, v);
      else params.delete(k);
    });
    if (updates.search !== undefined || updates.status !== undefined || updates.tag !== undefined) {
      params.set('page', '1');
      // Typing a search / picking a status is a NEW filter intent — release
      // the cross-page lookup so results aren't confusingly double-filtered.
      if (activeLookup) clearActiveLookup();
    }
    setSearchParams(params);
  };

  const showAllContacts = () => {
    clearActiveLookup();
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleDelete = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} contact(s)?`)) return;
    try {
      await api.delete('/contacts', { data: { contact_ids: ids } });
      setSelected(new Set());
      fetchContacts();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === contacts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(contacts.map(c => c.contact_id)));
    }
  };

  return (
    <div className="p-3 sm:p-6" data-testid="contacts-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-[#111827]" data-testid="contacts-title">
            Contacts
          </h1>
          <p className="text-sm text-[#4B5563] mt-0.5">{total.toLocaleString()} total</p>
        </div>
        <Button
          onClick={() => navigate('/contacts/new')}
          className="bg-[#002FA7] hover:bg-[#002080] text-white rounded-sm gap-1.5"
          data-testid="add-contact-btn"
        >
          <Plus size={16} weight="bold" />
          Add Contact
        </Button>
      </div>

      {/* Active lookup banner — the list is filtered to the current lookup */}
      {activeLookup && (
        <div
          className="mb-4 px-4 py-2.5 bg-[#FFF8E7] border border-[#F39C12]/50 rounded-sm flex items-center justify-between gap-3 flex-wrap"
          data-testid="list-lookup-banner"
        >
          <span className="text-sm text-[#7A4F00]">
            <strong>Lookup active:</strong> {activeLookup.label}
            {typeof activeLookup.count === 'number' && <> — {activeLookup.count.toLocaleString()} contact{activeLookup.count === 1 ? '' : 's'}</>}
          </span>
          <button
            onClick={showAllContacts}
            className="text-sm font-bold text-[#0B5394] hover:text-[#002FA7] underline underline-offset-2 whitespace-nowrap"
            data-testid="list-lookup-clear-btn"
          >
            Show all contacts
          </button>
        </div>
      )}

      {/* Filters Row */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
          <Input
            placeholder="Search contacts..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 rounded-sm focus:ring-1 focus:ring-[#002FA7]"
            data-testid="contact-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => updateParams({ status: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[140px] rounded-sm" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {statusOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportView}
          disabled={exporting || loading || total === 0}
          className="gap-1.5 rounded-sm border-[#0B5394]/40 text-[#0B5394] hover:bg-blue-50"
          data-testid="contact-list-export-btn"
          title={activeLookup ? 'Download the current lookup as CSV' : 'Download the current list as CSV'}
        >
          <DownloadSimple size={14} weight="bold" />
          {exporting ? 'Exporting…' : `Export CSV${total ? ` (${total.toLocaleString()})` : ''}`}
        </Button>
        {selected.size > 0 && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkEditOpen(true)}
              className="text-[#0B5394] border-[#0B5394]/40 hover:bg-blue-50 gap-1"
              data-testid="bulk-edit-btn"
            >
              <PencilSimple size={14} weight="bold" />
              Bulk Edit ({selected.size})
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDelete([...selected])}
              className="text-[#DC2626] border-red-200 hover:bg-red-50 gap-1"
              data-testid="bulk-delete-btn"
            >
              <Trash size={14} weight="bold" />
              Delete ({selected.size})
            </Button>
          </>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48" data-testid="contacts-loading">
            <div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-16" data-testid="no-contacts">
            <p className="text-sm text-[#4B5563] mb-3">No contacts found</p>
            <Button
              onClick={() => navigate('/contacts/new')}
              className="bg-[#002FA7] hover:bg-[#002080] text-white rounded-sm gap-1.5"
              data-testid="empty-add-contact-btn"
            >
              <Plus size={16} weight="bold" />
              Add Your First Contact
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="contacts-table">
                <thead>
                  <tr className="border-b border-[#D1D5DB] bg-[#F1F5F9]">
                    <th className="w-9 px-3 py-1.5 text-left">
                      <input
                        type="checkbox"
                        checked={selected.size === contacts.length && contacts.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded-sm"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-[#374151] uppercase tracking-wide">Name</th>
                    <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-[#374151] uppercase tracking-wide hidden md:table-cell">Company</th>
                    <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-[#374151] uppercase tracking-wide hidden lg:table-cell">Email</th>
                    <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-[#374151] uppercase tracking-wide hidden lg:table-cell">Phone</th>
                    <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-[#374151] uppercase tracking-wide">Status</th>
                    <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-[#374151] uppercase tracking-wide hidden xl:table-cell">Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c, idx) => (
                    <tr
                      key={c.contact_id}
                      className={`border-b border-[#E5E7EB] hover:bg-[#FFFBEB] cursor-pointer transition-colors ${idx % 2 === 1 ? 'bg-[#FAFBFC]' : 'bg-white'}`}
                      onClick={() => navigate(`/contacts/${c.contact_id}`)}
                      data-testid={`contact-row-${c.contact_id}`}
                    >
                      <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(c.contact_id)}
                          onChange={() => toggleSelect(c.contact_id)}
                          className="rounded-sm"
                          data-testid={`contact-checkbox-${c.contact_id}`}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="text-sm font-medium text-[#111827]">
                          {c.first_name} {c.last_name}
                        </span>
                        {/* Mobile-only: show company + email inline so the row is useful without scrolling */}
                        <span className="block md:hidden text-xs text-[#4B5563] mt-0.5 truncate max-w-[180px]">
                          {[c.company, c.email].filter(Boolean).join(' · ') || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 hidden md:table-cell">
                        <span className="text-sm text-[#4B5563]">{c.company || '—'}</span>
                      </td>
                      <td className="px-3 py-1.5 hidden lg:table-cell">
                        <span className="text-sm text-[#4B5563] flex items-center gap-1">
                          {c.email ? <><EnvelopeSimple size={13} />{c.email}</> : '—'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 hidden lg:table-cell">
                        <span className="text-sm text-[#4B5563] flex items-center gap-1">
                          {c.phone ? <><Phone size={13} />{c.phone}</> : '—'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5">
                        {c.status ? (
                          <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-sm border ${STATUS_COLORS[c.status] || STATUS_COLORS.lead}`}>
                            {c.status}
                          </span>
                        ) : (
                          <span className="text-[11px] text-[#9CA3AF]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 hidden xl:table-cell">
                        <div className="flex gap-1 flex-wrap">
                          {(c.tags || []).slice(0, 3).map(tag => (
                            <Badge key={tag} variant="secondary" className="text-[11px] rounded-sm px-1.5 py-0">
                              {tag}
                            </Badge>
                          ))}
                          {(c.tags || []).length > 3 && (
                            <span className="text-[11px] text-[#4B5563]">+{c.tags.length - 3}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E7EB]">
              <span className="text-xs text-[#4B5563]">
                Page {page} of {pages} ({total.toLocaleString()} contacts)
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => updateParams({ page: String(page - 1) })}
                  className="rounded-sm gap-1"
                  data-testid="prev-page-btn"
                >
                  <CaretLeft size={14} /> Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pages}
                  onClick={() => updateParams({ page: String(page + 1) })}
                  className="rounded-sm gap-1"
                  data-testid="next-page-btn"
                >
                  Next <CaretRight size={14} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <BulkEditDialog
        open={bulkEditOpen}
        contactIds={[...selected]}
        onClose={() => setBulkEditOpen(false)}
        onApplied={() => { setSelected(new Set()); fetchContacts(); }}
      />
    </div>
  );
}
